import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog'; // <-- Importeer MatDialogModule
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { RRule, Frequency, Weekday } from 'rrule';

export interface RecurrenceData {
  rruleString?: string;
  startDate: Date;
}

const ALL_WEEKDAYS: Weekday[] = [
  RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA, RRule.SU
];

@Component({
  selector: 'app-recurrence-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatButtonModule,
    MatDatepickerModule
  ],
  templateUrl: './recurrence-dialog.component.html',
  styleUrls: ['./recurrence-dialog.component.scss']
})
export class RecurrenceDialogComponent implements OnInit {
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' = 'WEEKLY';
  interval: number = 1;

  // Dagenkeuze
  weekDays = [
    { label: 'M', value: RRule.MO },
    { label: 'T', value: RRule.TU },
    { label: 'W', value: RRule.WE },
    { label: 'T', value: RRule.TH },
    { label: 'F', value: RRule.FR },
    { label: 'S', value: RRule.SA },
    { label: 'S', value: RRule.SU }
  ];

  selectedDays: Weekday[] = [];

  // Eindvoorwaarden
  endType: 'NEVER' | 'UNTIL' | 'COUNT' = 'NEVER';
  untilDate: Date = new Date();
  count: number = 10;

  constructor(
    public dialogRef: MatDialogRef<RecurrenceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RecurrenceData
  ) { }

  ngOnInit() {
    this.untilDate = new Date(this.data.startDate);
    this.untilDate.setMonth(this.untilDate.getMonth() + 3); // Standaard 3 maanden vooruit

    if (this.data.rruleString) {
      this.parseRRule(this.data.rruleString);
    } else {
      // Zet standaard de dag van de startdatum aan
      const dayIndex = (this.data.startDate.getDay() + 6) % 7; // Convert Sun=0 naar Mon=0
      this.selectedDays = [this.weekDays[dayIndex].value];
    }
  }

  toggleDay(day: Weekday) {
    const index = this.selectedDays.indexOf(day);
    if (index > -1) {
      if (this.selectedDays.length > 1) { // Minstens 1 dag geselecteerd houden
        this.selectedDays.splice(index, 1);
      }
    } else {
      this.selectedDays.push(day);
    }
  }

  parseRRule(rruleStr: string) {
    try {
      const rule = RRule.fromString(rruleStr);
      this.interval = rule.options.interval || 1;

      switch (rule.options.freq) {
        case Frequency.DAILY: this.frequency = 'DAILY'; break;
        case Frequency.WEEKLY: this.frequency = 'WEEKLY'; break;
        case Frequency.MONTHLY: this.frequency = 'MONTHLY'; break;
        case Frequency.YEARLY: this.frequency = 'YEARLY'; break;
      }

      if (rule.options.byweekday) {
        const rawDays = Array.isArray(rule.options.byweekday)
          ? rule.options.byweekday
          : [rule.options.byweekday];

        this.selectedDays = rawDays.map((day: any) => {
          // Pak het getal (0 t/m 6) van de dag
          const dayNum = typeof day === 'number' ? day : day.weekday;
          return ALL_WEEKDAYS[dayNum];
        });
      }

      if (rule.options.until) {
        this.endType = 'UNTIL';
        this.untilDate = rule.options.until;
      } else if (rule.options.count) {
        this.endType = 'COUNT';
        this.count = rule.options.count;
      } else {
        this.endType = 'NEVER';
      }
    } catch (e) {
      console.error('Error parsing RRule', e);
    }
  }

  save() {
    let freq: Frequency;
    switch (this.frequency) {
      case 'DAILY': freq = Frequency.DAILY; break;
      case 'WEEKLY': freq = Frequency.WEEKLY; break;
      case 'MONTHLY': freq = Frequency.MONTHLY; break;
      case 'YEARLY': freq = Frequency.YEARLY; break;
    }

    const options: any = {
      freq: freq,
      interval: this.interval,
      dtstart: this.data.startDate
    };

    if (this.frequency === 'WEEKLY' && this.selectedDays.length > 0) {
      options.byweekday = this.selectedDays;
    }

    if (this.endType === 'UNTIL' && this.untilDate) {
      // Zet de eindtijd op 23:59:59 van de gekozen datum
      const end = new Date(this.untilDate);
      end.setHours(23, 59, 59, 999);
      options.until = end;
    } else if (this.endType === 'COUNT' && this.count) {
      options.count = this.count;
    }

    const rule = new RRule(options);

    this.dialogRef.close({
      rruleString: rule.toString(),
      displayText: rule.toText() // Geeft bijv: "every week on Thursday until April 15, 2027"
    });
  }

  onFrequencyChange(): void {
    // Reset eventueel geselecteerde dagen of voer gewenste logica uit bij frequentiewijziging
    if (this.frequency !== 'WEEKLY') {
      // optioneel: logica wanneer van WEEKLY naar bijv. DAILY geschakeld wordt
    }
  }
}